// @flow

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import TouchBackend from 'react-dnd-touch-backend';
import { includes } from 'lodash';
import $ from 'jquery';
import { checkIE, randomAudioPlayer, shuffleArray } from '../utils';
import Container from './Container';
import DraggingPieceLayer from './DraggingPieceLayer';
import levels from './levels';
import useGameState, { STATES } from '../utils/use_game_state';
import Game from '../Shared/Game';
import useWindowSize from '../utils/use_window_size';
import type {
  AudioAssets,
  FinishedPieces,
  PieceData,
  PiecesData,
  Block,
} from './types';

type Props = {
  audio: AudioAssets,
  finishedPieces: FinishedPieces,
  piecesData: PiecesData,
  images: Array<string>,
  assets: Array<Array<string>>,
  assetPath: string,
  relatedGames: Array<any>,
  isApp: Boolean,
};

function selectBackend(): any {
  const html5 = HTML5Backend;
  const touch = TouchBackend({ enableMouseEvents: true });
  return checkIE() ? html5 : touch;
}

function createPiecesData(
  images: Array<string>,
  assets: Array<Array<string>>,
  level: number,
): PiecesData {
  const levelAssets = assets[level];
  return levels[level].blocks.map((block: Block, index: number): PieceData => ({
    width: block.width,
    height: block.height,
    pieceX: block.x,
    pieceY: block.y,
    glowOffset: -20,
    overlay: levelAssets[index],
    image: images[level],
    path: block.path,
    number: index,
  }));
}

function createFieldPieces(
  piecesData: PiecesData,
  finishedPieces: FinishedPieces,
): PiecesData {
  return piecesData.map(pieceData => ({
    finished: includes(finishedPieces, pieceData.number),
    ...pieceData,
  }));
}

function setFieldSize(
  puzzleWidth: number, windowHeight: number, windowWidth: number, portrait: Boolean,
): number {
  if (!portrait && puzzleWidth >= windowWidth * 0.6) {
    return puzzleWidth * 0.8;
  }
  if (portrait && puzzleWidth >= windowHeight * 0.5) return puzzleWidth * 0.8;
  return puzzleWidth;
}

const PuzzleGame = ({ audio, images, assets, assetPath, relatedGames, isApp }: Props) => {
  const windowSize = useWindowSize();
  const [level, setLevel] = useState(0);
  const [finishedPieces, setFinishedPieces] = useState([]);
  const [piecesData, setPiecesData] = useState(createPiecesData(images, assets, 0));
  const [shuffledPieces, setShuffledPieces] = useState(shuffleArray(piecesData));
  const [gameState, setGameDone] = useGameState();

  const rerenderMasks = () => {
    // Bug in Safari, does not render clippath after removal
    $('svg').hide().show(0);
  };

  const nextLevel = (): void => {
    const nextLevelIndex = level + 1;

    if (nextLevelIndex >= 4) {
      setFinishedPieces([]);
      setLevel(nextLevelIndex);
      setGameDone();
    } else {
      const nextPieces = createPiecesData(images, assets, nextLevelIndex);
      const nextShuffledPieces = shuffleArray(nextPieces);

      setFinishedPieces([]);
      setLevel(nextLevelIndex);
      setPiecesData(nextPieces);
      setShuffledPieces(nextShuffledPieces);
      rerenderMasks();

      const nextLevelAudio = new Audio(audio.harder);
      nextLevelAudio.play();
    }
  };

  // eslint-disable-next-line max-len
  const placePuzzlePiece = (placeNumber: number, droppedPieceNumber: number, cheat: boolean = false): void => {
    if (placeNumber === droppedPieceNumber) {
      if (includes(finishedPieces, droppedPieceNumber)) return;

      const newFinishedPieces = finishedPieces.concat(droppedPieceNumber);

      if (newFinishedPieces.length === levels[level].blocks.length) {
        nextLevel();
      } else {
        setFinishedPieces(newFinishedPieces);
        rerenderMasks();

        if (!cheat) {
          randomAudioPlayer(audio.right).play();
        }
      }
    } else {
      randomAudioPlayer(audio.wrong).play();
    }
  };

  const windowWidth = windowSize.width;
  const windowHeight = windowSize.height;
  const gameHeight = windowWidth > 1280 ? windowHeight - 130 : windowHeight;

  const puzzleWidth = () => {
    if (gameHeight > windowWidth) {
      return windowWidth * 0.9;
    }
    return gameHeight * 0.9;
  };
  const fieldWidth = setFieldSize(
    puzzleWidth(), gameHeight, windowWidth, windowHeight > windowWidth,
  );

  const puzzleFieldData = createFieldPieces(piecesData, finishedPieces);
  const filteredPieces = shuffledPieces.filter(
    piece => !includes(finishedPieces, piece.number),
  );
  const activePiece = filteredPieces[0];

  return (
    <Game gameState={gameState} relatedGames={relatedGames} assetPath={assetPath} isApp={isApp}>
      {gameState < STATES.DONE ? <Container
        piecesData={puzzleFieldData}
        finishedPieces={finishedPieces}
        placePuzzlePiece={placePuzzlePiece}
        level={level}
        activePiece={activePiece}
        fieldWidth={fieldWidth}
        helpAudio={audio.help}
        assetPath={assetPath}
      /> : null}
      {checkIE() || gameState >= STATES.DONE ? null : (
        <DraggingPieceLayer fieldWidth={fieldWidth} />
      )}
    </Game>
  );
};

const Puzzle = DragDropContext(selectBackend())(PuzzleGame);
const data = document.querySelector('#puzzle').dataset;
const props = {
  images: JSON.parse(data.images),
  assets: JSON.parse(data.assets),
  audio: JSON.parse(data.audio),
  assetPath: data.assetPath,
  isApp: data.app === 'true',
  relatedGames: JSON.parse(data.related),
};
ReactDOM.render(<Puzzle {...props} />, document.querySelector('#puzzle'));
